<template>
  <div class="p-4">
    <div class="bg-white rounded-lg shadow-md">
      <div class="p-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-800">KYT Transfer Requests</h2>
        
        <!-- Filters Section -->
        <div class="mt-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
              <input
                type="text"
                v-model="filters.employeeName"
                class="form-control"
                placeholder="Search by employee name"
              >
            </div>
            
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                v-model="filters.siteName"
                class="form-control"
                placeholder="Search by site name"
              >
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                v-model="filters.startDate"
                class="form-control"
              >
            </div>
            
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                v-model="filters.endDate"
                class="form-control"
              >
            </div>
          </div>
        </div>
      </div>
      
      <div class="table-responsive">
        <table class="display table-striped table-hover table-bordered table" id="basic-1">
          <thead>
            <tr>
              <th class="sorting_asc text-nowrap" scope="col">Sl No.</th>
              <th class="sorting_asc text-nowrap" scope="col">Site Name</th>
              <th class="sorting_asc text-nowrap" scope="col">Employee Name</th>
              <th class="sorting_asc text-nowrap" scope="col">Requested By</th>
              <th class="sorting_asc text-nowrap" scope="col">Transfer To</th>
              <th class="sorting_asc text-nowrap" scope="col">Priority</th>
              <th class="sorting_asc text-nowrap" scope="col">Status</th>
              <th class="sorting_asc text-nowrap" scope="col">Reason</th>
              <th class="sorting_asc text-nowrap" scope="col">Date</th>
              <th class="sorting_asc text-nowrap" scope="col">Approved By</th>
              <th class="sorting_asc text-nowrap" scope="col">Approved At</th>
              <th class="sorting_asc text-nowrap" scope="col">Images</th>
              <th class="sorting_asc text-nowrap" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(request, index) in filteredRequests" :key="request._id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.siteName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.empName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.requestedBy }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.transferTo || '-' }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.kytPriority }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="{
                  'px-2 py-1 text-xs font-semibold rounded-full text-black': true,
                  'bg-yellow-100': request.status === 'Pending',
                  'bg-green-100': request.status === 'Approved',
                  'bg-red-100': request.status === 'Rejected'
                }">
                  {{ request.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.reason }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(request.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ request.approvedBy?.name || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(request.approvedAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div v-if="request.images && request.images.length > 0" class="flex items-center justify-center">
                  <button 
                    @click="openGalleryModal(request.images)"
                    class="btn btn-light btn-sm"
                    :title="`View ${request.images.length} Image${request.images.length > 1 ? 's' : ''}`"
                  >
                    <i class="fa fa-images"></i>
                    <span class="ml-1 text-xs">{{ request.images.length }}</span>
                  </button>
                </div>
                <span v-else>-</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <button
                  @click="openDetailsModal(request)"
                  class="btn btn-info btn-sm"
                  title="View Details"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button
                  v-if="request.status === 'Pending'"
                  @click="handleApprove(request._id)"
                  class="btn btn-success btn-sm"
                >
                  Approve
                </button>
                <button
                  v-if="request.status === 'Pending'"
                  @click="openRejectModal(request._id)"
                  class="btn btn-danger btn-sm"
                >
                  Reject
                </button>
              </td>
            </tr>
            <tr v-if="filteredRequests.length === 0">
              <td colspan="13" class="text-center py-4 text-muted">No transfer requests found</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Show total results count -->
      <div class="p-4 border-t border-gray-200">
        <p class="text-sm text-gray-600">
          Showing {{ filteredRequests.length }} of {{ transferRequests.length }} total requests
        </p>
      </div>
    </div>

    <!-- Details Modal -->
    <div class="modal fade" id="detailsModal" tabindex="-1" aria-labelledby="detailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="detailsModalLabel">Transfer Request Details</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedRequest">
            <div class="grid grid-cols-2 gap-4">
              <div class="col-span-2 md:col-span-1">
                <h6 class="font-semibold">Basic Information</h6>
                <div class="mb-3">
                  <p><strong>Site Name:</strong> {{ selectedRequest.siteName }}</p>
                  <p><strong>Employee Name:</strong> {{ selectedRequest.empName }}</p>
                  <p><strong>Requested By:</strong> {{ selectedRequest.requestedBy }}</p>
                  <p><strong>Transfer To:</strong> {{ selectedRequest.transferTo || '-' }}</p>
                  <p><strong>Priority:</strong> {{ selectedRequest.kytPriority }}</p>
                  <p><strong>Status:</strong> {{ selectedRequest.status }}</p>
                  <p><strong>Type:</strong> {{ selectedRequest.type }}</p>
                  <p><strong>Reason:</strong> {{ selectedRequest.reason }}</p>
                  <p><strong>Created At:</strong> {{ formatDate(selectedRequest.createdAt) }}</p>
                </div>
              </div>
              <div class="col-span-2 md:col-span-1">
                <h6 class="font-semibold">Additional Information</h6>
                <div class="mb-3">
                  <p><strong>KYT Log No:</strong> {{ selectedRequest.kytLogNo || '-' }}</p>
                  <p><strong>Talked with Employee:</strong> {{ selectedRequest.takledWithEmp ? 'Yes' : 'No' }}</p>
                  <p><strong>Employee Accepted:</strong> {{ selectedRequest.hasEmpAccepted ? 'Yes' : 'No' }}</p>
                  <p><strong>Completed:</strong> {{ selectedRequest.isCompleted ? 'Yes' : 'No' }}</p>
                  <template v-if="selectedRequest.approvedBy">
                    <p><strong>Approved By:</strong> {{ selectedRequest.approvedBy.name }}</p>
                    <p><strong>Approved At:</strong> {{ formatDate(selectedRequest.approvedAt) }}</p>
                  </template>
                </div>
              </div>
              <div class="col-span-2" v-if="selectedRequest.images && selectedRequest.images.length > 0">
                <h6 class="font-semibold mb-3">Attached Images</h6>
                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div v-for="(image, index) in selectedRequest.images" :key="index" class="relative group">
                    <div class="h-48 w-full rounded-lg overflow-hidden shadow-lg">
                      <img 
                        :src="image" 
                        :alt="'Image ' + (index + 1)" 
                        class="w-full h-full object-contain bg-gray-100"
                        @click="openGalleryModal(selectedRequest.images, index)"
                      >
                    </div>
                    <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 rounded-lg"></div>
                    <div class="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button 
                        @click="openImageInNewTab(image)"
                        class="bg-white text-gray-800 rounded-full p-2 shadow-md hover:bg-gray-100"
                        title="View Full Image"
                      >
                        <i class="fa fa-expand"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject Modal -->
    <div class="modal fade" id="rejectModal" tabindex="-1" aria-labelledby="rejectModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="rejectModalLabel">Reject Transfer Request</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="rejectReason" class="form-label">Reason for Rejection</label>
              <textarea
                id="rejectReason"
                v-model="rejectReason"
                class="form-control"
                rows="3"
                placeholder="Enter reason for rejection"
                required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="handleReject" :disabled="!rejectReason">
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Gallery Modal -->
    <div class="modal fade" id="galleryModal" tabindex="-1" aria-labelledby="galleryModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content bg-gray-900">
          <div class="modal-header border-0 bg-gray-900 text-white">
            <h5 class="modal-title" id="galleryModalLabel">
              Image Gallery 
              <span class="text-gray-400 ml-2">
                {{ currentImageIndex + 1 }} of {{ galleryImages.length }}
              </span>
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0 bg-gray-900">
            <div class="relative gallery-container">
              <!-- Main Image Container -->
              <div class="image-viewer">
                <div class="image-loading" v-if="isImageLoading">
                  <div class="spinner-border text-light" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                <div class="image-error" v-if="imageLoadError">
                  <i class="fa fa-exclamation-circle text-danger"></i>
                  <p class="mt-2">Failed to load image</p>
                  <button class="btn btn-outline-light btn-sm mt-2" @click="retryLoadImage">
                    Retry
                  </button>
                </div>
                <img 
                  :src="currentGalleryImage" 
                  :key="currentGalleryImage"
                  class="main-gallery-image"
                  :class="{ 'hide': isImageLoading || imageLoadError }"
                  @load="handleImageLoad"
                  @error="handleImageError"
                  alt="Gallery Image"
                />
              </div>
              
              <!-- Navigation Buttons -->
              <button 
                v-if="galleryImages.length > 1"
                @click="previousImage" 
                class="nav-button nav-button-left"
                :disabled="isImageLoading"
              >
                <i class="fa fa-chevron-left"></i>
              </button>
              <button 
                v-if="galleryImages.length > 1"
                @click="nextImage" 
                class="nav-button nav-button-right"
                :disabled="isImageLoading"
              >
                <i class="fa fa-chevron-right"></i>
              </button>

              <!-- Thumbnails Bar -->
              <div 
                v-if="galleryImages.length > 1" 
                class="thumbnails-container"
              >
                <div class="thumbnails-scroll" ref="thumbnailsContainer">
                  <div 
                    v-for="(image, index) in galleryImages" 
                    :key="index"
                    @click="setCurrentImage(index)"
                    class="thumbnail-item"
                    :class="{ 'active': currentImageIndex === index }"
                  >
                    <img 
                      :src="image" 
                      :alt="'Thumbnail ' + (index + 1)"
                      class="thumbnail-image"
                      loading="lazy"
                    />
                    <div class="thumbnail-overlay" v-if="currentImageIndex === index">
                      <span class="thumbnail-number">{{ index + 1 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 bg-gray-900 text-white">
            <div class="flex-1 text-left">
              <span class="text-sm text-gray-400">
                Press <kbd class="px-2 py-1 bg-gray-800 rounded border border-gray-700">←</kbd> or <kbd class="px-2 py-1 bg-gray-800 rounded border border-gray-700">→</kbd> to navigate
              </span>
            </div>
            <button 
              class="btn btn-outline-light"
              @click="openImageInNewTab(currentGalleryImage)"
              :disabled="isImageLoading || imageLoadError"
            >
              <i class="fa fa-external-link-alt mr-1"></i>
              Open Full Size
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue'
import axios from 'axios'
import { Modal } from 'bootstrap'

interface KytId {
  _id: string
  siteName: string
  date: string
  status: string
}

interface EmpId {
  _id: string
  email: string
}

interface TransferRequest {
  _id: string
  kytId: KytId
  siteName: string
  isCompleted: boolean
  kytPriority: string
  empId: string
  empName: string
  requestedBy: string
  takledWithEmp: boolean
  hasEmpAccepted: boolean
  status: string
  type: string
  reason: string
  images: string[]
  transferTo: string
  createdAt: string
  updatedAt: string
  __v: number
  kytLogNo: string
  approvedBy: {
    name: string
    email: string
  } | null
  approvedAt: string | null
}

const transferRequests = ref<TransferRequest[]>([])
const rejectReason = ref('')
const selectedRequestId = ref('')
const selectedRequest = ref<TransferRequest | null>(null)
let rejectModal: any = null
let detailsModal: any = null
let galleryModal: any = null
const baseUrl = process.env.VUE_APP_BASE_URL
const token = localStorage.getItem('token')

const galleryImages = ref<string[]>([])
const currentImageIndex = ref(0)

const currentGalleryImage = computed(() => {
  return galleryImages.value[currentImageIndex.value] || ''
})

// Add these new refs
const isImageLoading = ref(true)
const imageLoadError = ref(false)
const thumbnailsContainer = ref<HTMLElement | null>(null)

// Add filter states
const filters = ref({
  employeeName: '',
  siteName: '',
  startDate: '',
  endDate: ''
})

// Add computed property for filtered requests
const filteredRequests = computed(() => {
  return transferRequests.value.filter(request => {
    const matchesEmployeeName = !filters.value.employeeName || 
      request.empName.toLowerCase().includes(filters.value.employeeName.toLowerCase())
    
    const matchesSiteName = !filters.value.siteName || 
      request.siteName.toLowerCase().includes(filters.value.siteName.toLowerCase())
    
    const requestDate = new Date(request.createdAt)
    const startDate = filters.value.startDate ? new Date(filters.value.startDate) : null
    const endDate = filters.value.endDate ? new Date(filters.value.endDate) : null
    
    const matchesDateRange = (!startDate || requestDate >= startDate) && 
      (!endDate || requestDate <= new Date(endDate.setHours(23, 59, 59, 999)))
    
    return matchesEmployeeName && matchesSiteName && matchesDateRange
  })
})

// Add keyboard navigation
const handleKeyPress = (e: KeyboardEvent) => {
  if (galleryModal && galleryImages.value.length > 1 && !isImageLoading.value) {
    if (e.key === 'ArrowLeft') {
      previousImage()
    } else if (e.key === 'ArrowRight') {
      nextImage()
    }
  }
}

// Add these new methods
const handleImageLoad = () => {
  isImageLoading.value = false
  imageLoadError.value = false
}

const handleImageError = () => {
  isImageLoading.value = false
  imageLoadError.value = true
}

const retryLoadImage = () => {
  isImageLoading.value = true
  imageLoadError.value = false
  // Force image reload by creating a new URL with cache buster
  const currentImage = galleryImages.value[currentImageIndex.value]
  galleryImages.value[currentImageIndex.value] = `${currentImage}?t=${Date.now()}`
}

const setCurrentImage = (index: number) => {
  isImageLoading.value = true
  imageLoadError.value = false
  currentImageIndex.value = index
  scrollThumbnailIntoView(index)
}

const scrollThumbnailIntoView = (index: number) => {
  if (!thumbnailsContainer.value) return
  
  const thumbnailElement = thumbnailsContainer.value.children[index] as HTMLElement
  if (!thumbnailElement) return

  const containerWidth = thumbnailsContainer.value.clientWidth
  const thumbnailWidth = thumbnailElement.offsetWidth
  const scrollLeft = thumbnailElement.offsetLeft - (containerWidth / 2) + (thumbnailWidth / 2)
  
  thumbnailsContainer.value.scrollTo({
    left: scrollLeft,
    behavior: 'smooth'
  })
}

onMounted(() => {
  fetchTransferRequests()
  rejectModal = new Modal(document.getElementById('rejectModal'))
  detailsModal = new Modal(document.getElementById('detailsModal'))
  galleryModal = new Modal(document.getElementById('galleryModal'))
  
  // Add keyboard event listener
  window.addEventListener('keydown', handleKeyPress)
})

// Clean up event listener
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress)
})

const fetchTransferRequests = async () => {
  try {
    const response = await axios.get(`${baseUrl}api/operation/kyt/transfer-requests`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (response.data.success) {
      transferRequests.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching transfer requests:', error)
    alert('Error fetching transfer requests')
  }
}

const handleApprove = async (requestId: string) => {
  try {
    const response = await axios.put(
      `${baseUrl}api/operation/kyt/transfer-requests/approve/${requestId}`,
      {}, // Empty body
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    )
    if (response.data.success) {
      alert('Transfer request approved successfully')
      await fetchTransferRequests()
    }
  } catch (error: any) {
    console.error('Error approving request:', error)
    alert(error.response?.data?.message || 'Error approving request')
  }
}

const openRejectModal = (requestId: string) => {
  selectedRequestId.value = requestId
  rejectReason.value = ''
  rejectModal?.show()
}

const handleReject = async () => {
  if (!rejectReason.value) {
    alert('Please provide a reason for rejection')
    return
  }

  try {
    const response = await axios.put(
      `${baseUrl}api/operation/kyt/transfer-requests/reject/${selectedRequestId.value}`,
      { reason: rejectReason.value },
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    )
    if (response.data.success) {
      alert('Transfer request rejected successfully')
      rejectModal?.hide()
      await fetchTransferRequests()
    }
  } catch (error: any) {
    console.error('Error rejecting request:', error)
    alert(error.response?.data?.message || 'Error rejecting request')
  }
}

const openDetailsModal = (request: TransferRequest) => {
  selectedRequest.value = request
  detailsModal?.show()
}

const openImageInNewTab = (imageUrl: string) => {
  window.open(imageUrl, '_blank')
}

const openGalleryModal = (images: string[], startIndex: number = 0) => {
  galleryImages.value = images
  isImageLoading.value = true
  imageLoadError.value = false
  currentImageIndex.value = startIndex
  galleryModal?.show()
}

const nextImage = () => {
  if (isImageLoading.value) return
  const nextIndex = currentImageIndex.value < galleryImages.value.length - 1 ? currentImageIndex.value + 1 : 0
  setCurrentImage(nextIndex)
}

const previousImage = () => {
  if (isImageLoading.value) return
  const prevIndex = currentImageIndex.value > 0 ? currentImageIndex.value - 1 : galleryImages.value.length - 1
  setCurrentImage(prevIndex)
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table th {
  font-weight: 600;
  background-color: #f8f9fa;
}

.table td {
  vertical-align: middle;
}

@media (max-width: 640px) {
  .table {
    display: block;
    width: 100%;
  }
  
  th, td {
    min-width: 120px;
  }
}

/* Gallery modal styles */
.modal-xl {
  max-width: 95vw;
  margin: 1.75rem auto;
}

.modal-dialog-centered {
  min-height: calc(100% - 3.5rem);
}

/* Updated Gallery Styles */
.gallery-container {
  width: 100%;
  height: calc(100vh - 200px);
  position: relative;
  background-color: #111827;
}

.image-viewer {
  width: 100%;
  height: calc(100% - 100px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.main-gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: opacity 0.3s ease;
}

.main-gallery-image.hide {
  opacity: 0;
}

.image-loading,
.image-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-button:hover {
  background: rgba(0, 0, 0, 0.8);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-button-left {
  left: 20px;
}

.nav-button-right {
  right: 20px;
}

.thumbnails-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(17, 24, 39, 0.95);
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.thumbnails-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 5px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.thumbnails-scroll::-webkit-scrollbar {
  display: none;
}

.thumbnail-item {
  flex: 0 0 auto;
  width: 60px;
  height: 60px;
  position: relative;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.thumbnail-item:hover {
  transform: translateY(-2px);
}

.thumbnail-item.active {
  border: 2px solid #3b82f6;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-overlay {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .gallery-container {
    height: calc(100vh - 120px);
  }

  .nav-button {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
  }

  .thumbnail-item {
    width: 50px;
    height: 50px;
  }
}

/* Add these new styles */
.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.form-group {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .grid-cols-4 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  
  .form-group {
    margin-bottom: 0.75rem;
  }
}

/* Improve table styles */
.table {
  width: 100%;
  margin-bottom: 1rem;
  background-color: transparent;
  border-collapse: collapse;
}

.table th {
  padding: 0.75rem;
  font-weight: 600;
  background-color: #f3f4f6;
  border-bottom: 2px solid #e5e7eb;
}

.table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Improve button styles */
.btn {
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>

